export interface ValidationResult {
    isValid: boolean;
    error?: string;
    blockedChars?: string[];
}

export interface FieldValidationRule {
    pattern?: RegExp;
    blockedPattern?: RegExp;
    blockedPatterns?: RegExp[];
    maxLength: number;
    minLength?: number;
    required?: boolean;
    errorMessage: string;
}

export const VALIDATION_RULES: Record<string, FieldValidationRule> = {
    name: {
        pattern: /^[a-zA-Z\s\-']+$/,
        blockedPattern: /[0-9@#$%^&*()_+={}[\]|\\:;"<>?/~`!]/g,
        maxLength: 100,
        minLength: 2,
        errorMessage: 'Only letters, spaces, hyphens allowed'
    },
    certifications: {
        pattern: /^[a-zA-Z0-9\s,.\-/&()]+$/,
        blockedPattern: /[@#$%^*_+={}[\]|\\:;"'<>?~`!]/g,
        maxLength: 500,
        errorMessage: 'Special characters not allowed'
    },
    roleCompany: {
        pattern: /^[a-zA-Z0-9\s.\-/&]+$/,
        blockedPattern: /[@#$%^*_+={}[\]|\\:;"'<>?~`!()]/g,
        maxLength: 100,
        errorMessage: 'Special characters not allowed'
    },
    careerAspiration: {
        pattern: /^[a-zA-Z0-9\s\-/]+$/,
        blockedPattern: /[@#$%^&*_+={}[\]|\\:;"'<>?~`!.,()]/g,
        maxLength: 150,
        errorMessage: 'Special characters not allowed'
    },
    location: {
        pattern: /^[a-zA-Z0-9\s,\-]+$/,
        blockedPattern: /[@#$%^&*_+={}[\]|\\:;"'<>?~`!./()]/g,
        maxLength: 200,
        errorMessage: 'Special characters not allowed'
    },
    // TTEC Communities - Only allows letters, numbers, spaces, comma, hyphen, period
    ttecCommunities: {
        pattern: /^[A-Za-z0-9\s,.\-]*$/,
        blockedPattern: /[^A-Za-z0-9\s,.\-]/g,
        blockedPatterns: [
            /<script/gi,
            /javascript:/gi,
            /onerror\s*=/gi,
            /onload\s*=/gi,
            /onclick\s*=/gi,
            /onmouseover\s*=/gi,
            /<iframe/gi,
            /<object/gi,
            /<img/gi,
            /<svg/gi,
            /<embed/gi,
        ],
        maxLength: 200,
        errorMessage: 'Special Characers are not allowed.'
    },
    // Performance Summary - Allows letters, numbers, spaces, period, comma, !, ?, :, ;, -, ', (), newlines
    performanceSummary: {
        pattern: /^[A-Za-z0-9\s.,!?:;\-'()\n\r]*$/,
        blockedPattern: /[^A-Za-z0-9\s.,!?:;\-'()\n\r]/g,
        blockedPatterns: [
            /<script/gi,
            /javascript:/gi,
            /onerror\s*=/gi,
            /onload\s*=/gi,
            /onclick\s*=/gi,
            /onmouseover\s*=/gi,
            /<iframe/gi,
            /<object/gi,
            /<img/gi,
            /<svg/gi,
            /<embed/gi,
            /['"];\s*--/g,
            /\/\*.*\*\//g,
        ],
        maxLength: 2000,
        errorMessage: 'special characters are are not allowed.'
    },
    performanceText: {
        blockedPatterns: [
            /['"];\s*--/g,
            /\/\*.*\*\//g,
            /<script/gi,
            /javascript:/gi,
            /onerror\s*=/gi,
            /onload\s*=/gi,
            /onclick\s*=/gi,
            /onmouseover\s*=/gi,
            /<iframe/gi,
            /<object/gi,
        ],
        maxLength: 2000,
        minLength: 10,
        errorMessage: 'Contains potentially unsafe content'
    },
    year: {
        pattern: /^(\d{4}|Present)$/i,
        maxLength: 10,
        errorMessage: 'Must be 4-digit year or "Present"'
    },
    timeInRole: {
        pattern: /^[a-zA-Z0-9\s.\-]+$/,
        blockedPattern: /[@#$%^&*()_+={}[\]|\\:;"'<>?/~`!,]/g,
        maxLength: 50,
        errorMessage: 'Special characters not allowed'
    },
    education: {
        pattern: /^[a-zA-Z0-9\s.,'\-()]+$/,
        blockedPattern: /[@#$%^&*_+={}[\]|\\:;"<>?/~`!]/g,
        maxLength: 100,
        errorMessage: 'Special characters not allowed'
    },
    generalText: {
        pattern: /^[a-zA-Z0-9\s.,\-()/'&]+$/,
        blockedPattern: /[@#$%^*_+={}[\]|\\:;"<>?~`!]/g,
        maxLength: 500,
        errorMessage: 'Special characters not allowed'
    },
    description: {
        blockedPattern: /[@#$%^*_+={}[\]|\\<>~`]/g,
        maxLength: 500,
        errorMessage: 'Special characters not allowed'
    }
};

export const FIELD_RULE_MAPPING: Record<string, keyof typeof VALIDATION_RULES> = {
    'time_in_current_role': 'timeInRole',
    'certificates': 'certifications',
    'education_degree': 'education',
    'current_location': 'location',
    'willingness_to_relocate': 'generalText',
    'location_mobility': 'location',
    'next_role_preference': 'careerAspiration',
    'future_career_aspirations': 'careerAspiration',
    'timing_for_next_role': 'generalText',
    'company_name': 'roleCompany',
    'job_title': 'roleCompany',
    'role_title': 'roleCompany',
    'job_description': 'description',
    'description': 'description',
    'start_year': 'year',
    'end_year': 'year',
    'ttec_communities': 'ttecCommunities',
    'ttecCommunity': 'ttecCommunities',
    'last_year_personal_development_review': 'performanceSummary',
    'goal_next_two_quarters': 'performanceSummary',
    'accomplishments': 'performanceSummary',
    'improvements': 'performanceSummary',
    'goals': 'performanceSummary',
    'redo': 'performanceSummary',
    'redoBusiness': 'performanceSummary',
    'last_year_top_achievement': 'performanceSummary',
    'redo_for_previous_business_year': 'performanceSummary'
};

function findBlockedCharacters(value: string, blockedPattern: RegExp): string[] {
    const matches = value.match(blockedPattern);
    if (!matches) return [];
    return Array.from(new Set(matches));
}

export function validateField(
    fieldName: string,
    value: string | null | undefined,
    customRuleType?: keyof typeof VALIDATION_RULES
): ValidationResult {
    if (!value || value.trim() === '') {
        return { isValid: true };
    }

    const trimmedValue = value.trim();
    const ruleType = customRuleType || FIELD_RULE_MAPPING[fieldName] || 'generalText';
    const rule = VALIDATION_RULES[ruleType];

    if (!rule) {
        return { isValid: true };
    }

    if (trimmedValue.length > rule.maxLength) {
        return {
            isValid: false,
            error: `Maximum ${rule.maxLength} characters allowed`
        };
    }

    if (rule.blockedPatterns) {
        for (const pattern of rule.blockedPatterns) {
            if (pattern.test(trimmedValue)) {
                return {
                    isValid: false,
                    error: rule.errorMessage
                };
            }
        }
        return { isValid: true };
    }

    if (rule.blockedPattern) {
        const blockedChars = findBlockedCharacters(trimmedValue, rule.blockedPattern);
        if (blockedChars.length > 0) {
            return {
                isValid: false,
                error: rule.errorMessage,
                blockedChars
            };
        }
    }

    return { isValid: true };
}

export function sanitizeInput(
    fieldName: string,
    value: string,
    customRuleType?: keyof typeof VALIDATION_RULES
): string {
    if (!value) return '';

    const ruleType = customRuleType || FIELD_RULE_MAPPING[fieldName] || 'generalText';
    const rule = VALIDATION_RULES[ruleType];

    if (!rule) return value.trim();

    let sanitized = value;

    if (rule.blockedPatterns) {
        for (const pattern of rule.blockedPatterns) {
            sanitized = sanitized.replace(pattern, '');
        }
        return sanitized.trim();
    }

    if (rule.blockedPattern) {
        sanitized = value.replace(rule.blockedPattern, '');
    }

    return sanitized.trim();
}

export function validateMultipleFields(
    data: Record<string, any>,
    fieldsToValidate?: string[]
): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};
    const fields = fieldsToValidate || Object.keys(FIELD_RULE_MAPPING);

    for (const fieldName of fields) {
        const value = data[fieldName];
        if (value !== undefined && value !== null && typeof value === 'string') {
            const result = validateField(fieldName, value);
            if (!result.isValid && result.error) {
                errors[fieldName] = result.error;
            }
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}

export function isBlockedCharacter(char: string): boolean {
    const generalBlockedPattern = /[@#$%^*_+={}[\]|\\:;"'<>?~`!]/;
    return generalBlockedPattern.test(char);
}

export function getValidationErrorMessage(fieldName: string): string {
    const ruleType = FIELD_RULE_MAPPING[fieldName] || 'generalText';
    const rule = VALIDATION_RULES[ruleType];
    return rule?.errorMessage || 'Special characters not allowed';
}

export const SPECIAL_CHAR_ERROR_MESSAGE = 'Special characters not allowed';

export function detectBlockedCharacters(
    fieldName: string,
    value: string | null | undefined,
    customRuleType?: keyof typeof VALIDATION_RULES
): string[] {
    if (!value || value.trim() === '') return [];

    const ruleType = customRuleType || FIELD_RULE_MAPPING[fieldName] || 'generalText';
    const rule = VALIDATION_RULES[ruleType];
    if (!rule) return [];

    const trimmedValue = value.trim();
    if (rule.blockedPatterns) {
        for (const pattern of rule.blockedPatterns) {
            if (pattern.test(trimmedValue)) {
                return ['unsafe content'];
            }
        }
    }
    if (rule.blockedPattern) {
        return findBlockedCharacters(trimmedValue, rule.blockedPattern);
    }

    return [];
}

export interface SavedDataWarning {
    field: string;
    displayName: string;
    blockedChars: string[];
}

const FIELD_DISPLAY_NAMES: Record<string, string> = {
    current_location: 'Current Location',
    willingness_to_relocate: 'Willingness to Relocate',
    next_role_preference: 'Next Role Preference',
    future_career_aspirations: 'Future Career Aspirations',
    timing_for_next_role: 'Timing for Next Role',
    certificates: 'Certifications',
    education_degree: 'Education Degree',
    time_in_current_role: 'Time in Current Role',
    last_year_top_achievement: 'Top Achievement',
    last_year_personal_development_review: 'Personal Development Review',
    redo_for_previous_business_year: 'Redo for Previous Business Year',
    goal_next_two_quarters: 'Goals for Next Two Quarters',
    ttecCommunity: 'TTEC Communities',
};

export function scanSavedDataForBlockedChars(data: Record<string, any>): SavedDataWarning[] {
    const warnings: SavedDataWarning[] = [];

    for (const [fieldKey, displayName] of Object.entries(FIELD_DISPLAY_NAMES)) {
        const value = data[fieldKey];
        if (!value || typeof value !== 'string' || value.trim() === '') continue;

        const blockedChars = detectBlockedCharacters(fieldKey, value);
        if (blockedChars.length > 0) {
            warnings.push({
                field: fieldKey,
                displayName,
                blockedChars
            });
        }
    }

    return warnings;
}

/**
 * ValidationResult interface for TTEC Communities and Performance Summary validators
 */
export interface TTECValidationResult {
    isValid: boolean;
    errorMessage: string | null;
}

/**
 * Validate TTEC Communities field
 * Allowed characters: Letters (A-Z, a-z), Numbers (0-9), Spaces, Comma (,), Hyphen (-), Period (.)
 * Blocks: HTML tags (<script>, <img>, etc.), @ # $ % ^ & * [ ] { } ( ) | \
 * @param value - The value to validate
 * @returns ValidationResult with isValid and errorMessage
 */
export function validateTTECCommunities(value: string | null | undefined): TTECValidationResult {
    if (value === null || value === undefined || value.trim() === '') {
        return { isValid: true, errorMessage: null };
    }

    const trimmedValue = value.trim();
    
    // Check for XSS/HTML injection patterns first
    const xssPatterns = [
        /<script/gi,
        /javascript:/gi,
        /onerror\s*=/gi,
        /onload\s*=/gi,
        /onclick\s*=/gi,
        /onmouseover\s*=/gi,
        /<iframe/gi,
        /<object/gi,
        /<img/gi,
        /<svg/gi,
        /<embed/gi,
        /<link/gi,
        /<style/gi,
        /<meta/gi,
        /data:/gi,
        /vbscript:/gi,
    ];

    for (const pattern of xssPatterns) {
        if (pattern.test(trimmedValue)) {
            return { isValid: false, errorMessage: 'Special Characers are not allowed.' };
        }
    }

    // Only allow: Letters (A-Z, a-z), Numbers (0-9), Spaces, Comma, Hyphen, Period
    const allowedPattern = /^[A-Za-z0-9\s,.\-]*$/;
    
    if (!allowedPattern.test(trimmedValue)) {
        return { isValid: false, errorMessage: 'Special Characers are not allowed.' };
    }

    return { isValid: true, errorMessage: null };
}

/**
 * Validate Performance Summary fields (4 Questions)
 * Allowed characters: Letters, Numbers, Spaces, Period, Comma, !, ?, :, ;, -, ', ()
 * Blocks: HTML tags (<script>, <img>, etc.), @ # $ % ^ & * [ ] { } | \
 * @param value - The value to validate
 * @returns ValidationResult with isValid and errorMessage
 */
export function validatePerformanceSummary(value: string | null | undefined): TTECValidationResult {
    if (value === null || value === undefined || value.trim() === '') {
        return { isValid: true, errorMessage: null };
    }

    const trimmedValue = value.trim();
    
    // Check for XSS/HTML injection patterns first
    const xssPatterns = [
        /<script/gi,
        /javascript:/gi,
        /onerror\s*=/gi,
        /onload\s*=/gi,
        /onclick\s*=/gi,
        /onmouseover\s*=/gi,
        /<iframe/gi,
        /<object/gi,
        /<img/gi,
        /<svg/gi,
        /<embed/gi,
        /<link/gi,
        /<style/gi,
        /<meta/gi,
        /data:/gi,
        /vbscript:/gi,
        /['"];\s*--/g,
        /\/\*.*\*\//g,
    ];

    for (const pattern of xssPatterns) {
        if (pattern.test(trimmedValue)) {
            return { isValid: false, errorMessage: 'special characters are are not allowed.' };
        }
    }

    // Only allow: Letters, Numbers, Spaces, Period, Comma, !, ?, :, ;, -, ', (), newlines
    const allowedPattern = /^[A-Za-z0-9\s.,!?:;\-'()\n\r]*$/;
    
    if (!allowedPattern.test(trimmedValue)) {
        return { isValid: false, errorMessage: 'special characters are are not allowed.' };
    }

    return { isValid: true, errorMessage: null };
}

/**
 * Sanitize TTEC Communities input by removing disallowed characters
 * @param value - The value to sanitize
 * @returns Sanitized string
 */
export function sanitizeTTECCommunities(value: string | null | undefined): string {
    if (value === null || value === undefined) return '';
    
    let sanitized = value;
    
    // Remove HTML tags
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    
    // Remove all characters except allowed: Letters, Numbers, Spaces, Comma, Hyphen, Period
    sanitized = sanitized.replace(/[^A-Za-z0-9\s,.\-]/g, '');
    
    return sanitized.trim();
}

/**
 * Sanitize Performance Summary input by removing disallowed characters
 * @param value - The value to sanitize
 * @returns Sanitized string
 */
export function sanitizePerformanceSummary(value: string | null | undefined): string {
    if (value === null || value === undefined) return '';
    
    let sanitized = value;
    
    // Remove HTML tags
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    
    // Remove all characters except allowed: Letters, Numbers, Spaces, Period, Comma, !, ?, :, ;, -, ', (), newlines
    sanitized = sanitized.replace(/[^A-Za-z0-9\s.,!?:;\-'()\n\r]/g, '');
    
    return sanitized.trim();
}
